import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Alert,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  TextField,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Radio,
  RadioGroup,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUserSettings,
  saveUserSettings,
  setLocalSetting,
  USER_SETTING_EVENT_NOTIFICATIONS,
  USER_SETTING_PREVIEW_FEATURES,
  USER_SETTING_CHART_PREFERENCES,
  USER_SETTING_SEMI_LIVE_CANDLES,
  selectUserSettingsSynced,
} from './userSettingsSlice';
import {
  AXIS_COLOR_SCHEMES,
  DEFAULT_AXIS_COLOR_SCHEME,
  CROSSHAIR_SNAP_MODES,
  normalizeCrosshairSnapMode,
  normalizeCrosshairPriceLabelPreference,
  normalizeAdaptiveGridPreference,
} from '../../constants/chartPreferences';
import {
  DWLF_EVENT_TYPES,
  INDICATOR_EVENT_DEFINITIONS,
} from '../../constants/eventTypes';
import userTradeSignalsAPI from '../../api/userTradeSignals';
import customEventsApi from '../../api/customEvents';
import QuotaStatusPanel from '../quotas/QuotaStatusPanel';
import ApiKeysTab from './ApiKeysTab';
import TelegramBotCard from './TelegramBotCard';
import SubscriptionsSection from './SubscriptionsSection';
import TradingDefaultsTab from './TradingDefaultsTab';
import BriefingListTab from './BriefingListTab';
import DWLFLoader from '../../components/DWLFLoader';
import usePermissions from '../../hooks/usePermissions';

const EVENT_CATEGORY_LABELS = Object.freeze({
  swings: 'Swing Structure',
  trendlines: 'Trendlines',
  marketStructure: 'Support & Resistance',
  movingAverages: 'Moving Averages',
  vwap: 'VWAP',
  oscillators: 'Momentum Oscillators',
  volatility: 'Volatility Signals',
  other: 'Other Events',
});

const UserSettingsPage = () => {
  const dispatch = useDispatch();
  const { isAdmin } = usePermissions();
  const isAdminUser = isAdmin();
  const { settings, loading, error, saving, saveError } = useSelector((state) => state.userSettings);
  // Save sends the whole settings map, chartPreferences included, which the API stores in place
  // of the server's; only allowed once that map really was read for this account.
  const settingsSynced = useSelector(selectUserSettingsSynced);
  const [activeTab, setActiveTab] = useState('notifications');
  const [purgeWeeks, setPurgeWeeks] = useState(12);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeFeedback, setPurgeFeedback] = useState(null);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);

  // Animation preferences state
  const [animationEnabled, setAnimationEnabled] = useState(() => 
    localStorage.getItem('dwlf-animation-enabled') === 'true'
  );

  // Custom events notification state
  const [customEvents, setCustomEvents] = useState([]);
  const [customEventsLoading, setCustomEventsLoading] = useState(false);
  const [customEventsError, setCustomEventsError] = useState(null);
  const [toggleError, setToggleError] = useState(null);
  const [togglingEventIds, setTogglingEventIds] = useState(new Set());

  useEffect(() => {
    dispatch(fetchUserSettings());
  }, [dispatch]);

  // Fetch custom events when the customEvents tab is active
  const fetchCustomEvents = useCallback(async () => {
    setCustomEventsLoading(true);
    setCustomEventsError(null);
    try {
      const result = await customEventsApi.listEvents({ limit: 100 });
      setCustomEvents(result.events || []);
    } catch (err) {
      setCustomEventsError(err?.response?.data?.error || err?.message || 'Failed to fetch custom events');
    } finally {
      setCustomEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'customEvents') {
      fetchCustomEvents();
    }
  }, [activeTab, fetchCustomEvents]);

  const handleToggleNotifyOnFire = async (eventId, currentValue) => {
    setTogglingEventIds((prev) => new Set(prev).add(eventId));
    setToggleError(null); // Clear any previous toggle error
    try {
      await customEventsApi.patchEvent(eventId, { notifyOnFire: !currentValue });
      setCustomEvents((prev) =>
        prev.map((evt) =>
          evt.eventId === eventId ? { ...evt, notifyOnFire: !currentValue } : evt
        )
      );
    } catch (err) {
      setToggleError(err?.response?.data?.error || err?.message || 'Failed to update notification setting');
    } finally {
      setTogglingEventIds((prev) => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
    }
  };

  const eventNotifications = useMemo(() => {
    const raw = settings?.[USER_SETTING_EVENT_NOTIFICATIONS];
    if (Array.isArray(raw)) {
      return raw;
    }
    return [];
  }, [settings]);

  const previewFeaturesEnabled = settings?.[USER_SETTING_PREVIEW_FEATURES] === true;

  const chartPreferencesStore = useMemo(() => {
    const raw = settings?.[USER_SETTING_CHART_PREFERENCES];
    if (raw && typeof raw === 'object') {
      return raw;
    }
    return {};
  }, [settings]);

  const globalChartPreferences = useMemo(() => {
    const raw = chartPreferencesStore?.__global;
    if (raw && typeof raw === 'object') {
      return raw;
    }
    return {};
  }, [chartPreferencesStore]);

  const categorizedIndicatorEvents = useMemo(() => {
    const groups = new Map();
    INDICATOR_EVENT_DEFINITIONS.forEach((definition) => {
      if (!definition?.id) {
        return;
      }
      const categoryKey = definition.category || 'other';
      const label = EVENT_CATEGORY_LABELS[categoryKey] || EVENT_CATEGORY_LABELS.other;
      if (!groups.has(label)) {
        groups.set(label, []);
      }
      groups.get(label).push(definition);
    });

    return Array.from(groups.entries()).map(([label, definitions]) => ({
      label,
      definitions: definitions.slice().sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, []);

  const axisColorSchemeId = globalChartPreferences.axisColorScheme || DEFAULT_AXIS_COLOR_SCHEME;
  const crosshairSnapModeId = normalizeCrosshairSnapMode(globalChartPreferences.crosshairSnapMode);
  const showCrosshairPriceLabel = normalizeCrosshairPriceLabelPreference(
    globalChartPreferences.showCrosshairPriceLabel,
  );
  const showAdaptiveGrid = normalizeAdaptiveGridPreference(globalChartPreferences.showAdaptiveGrid);
  const showAdminChartControls = globalChartPreferences.showAdminChartControls === true;

  const handleToggle = (eventType) => {
    const current = new Set(eventNotifications);
    if (current.has(eventType)) {
      current.delete(eventType);
    } else {
      current.add(eventType);
    }
    dispatch(setLocalSetting({ settingKey: USER_SETTING_EVENT_NOTIFICATIONS, value: Array.from(current) }));
  };

  const handleSelectAll = () => {
    dispatch(setLocalSetting({ settingKey: USER_SETTING_EVENT_NOTIFICATIONS, value: [...DWLF_EVENT_TYPES] }));
  };

  const handleClearAll = () => {
    dispatch(setLocalSetting({ settingKey: USER_SETTING_EVENT_NOTIFICATIONS, value: [] }));
  };

  const handlePreviewToggle = (_, checked) => {
    dispatch(setLocalSetting({ settingKey: USER_SETTING_PREVIEW_FEATURES, value: checked }));
  };

  const handleAxisColorSchemeChange = (event) => {
    const schemeId = event?.target?.value;
    if (!schemeId) {
      return;
    }

    if (schemeId === axisColorSchemeId) {
      return;
    }

    const nextStore = {
      ...chartPreferencesStore,
      __global: {
        ...globalChartPreferences,
        axisColorScheme: schemeId,
      },
    };

    dispatch(
      setLocalSetting({
        settingKey: USER_SETTING_CHART_PREFERENCES,
        value: nextStore,
      }),
    );
  };

  const handleCrosshairSnapModeChange = (event) => {
    const mode = normalizeCrosshairSnapMode(event?.target?.value);
    if (!mode) {
      return;
    }

    if (mode === crosshairSnapModeId) {
      return;
    }

    const nextStore = {
      ...chartPreferencesStore,
      __global: {
        ...globalChartPreferences,
        crosshairSnapMode: mode,
      },
    };

    dispatch(
      setLocalSetting({
        settingKey: USER_SETTING_CHART_PREFERENCES,
        value: nextStore,
      }),
    );
  };

  const handleCrosshairPriceLabelToggle = (_, checked) => {
    const nextStore = {
      ...chartPreferencesStore,
      __global: {
        ...globalChartPreferences,
        showCrosshairPriceLabel: Boolean(checked),
      },
    };

    dispatch(
      setLocalSetting({
        settingKey: USER_SETTING_CHART_PREFERENCES,
        value: nextStore,
      }),
    );
  };

  const handleAdaptiveGridToggle = (_, checked) => {
    const nextStore = {
      ...chartPreferencesStore,
      __global: {
        ...globalChartPreferences,
        showAdaptiveGrid: Boolean(checked),
      },
    };

    dispatch(
      setLocalSetting({
        settingKey: USER_SETTING_CHART_PREFERENCES,
        value: nextStore,
      }),
    );
  };

  const handleAdminChartControlsToggle = (_, checked) => {
    const nextStore = {
      ...chartPreferencesStore,
      __global: {
        ...globalChartPreferences,
        showAdminChartControls: Boolean(checked),
      },
    };

    dispatch(
      setLocalSetting({
        settingKey: USER_SETTING_CHART_PREFERENCES,
        value: nextStore,
      }),
    );
  };

  const handleSave = () => {
    if (!settingsSynced) return;
    const payload = {
      ...settings,
      [USER_SETTING_EVENT_NOTIFICATIONS]: eventNotifications,
    };
    dispatch(saveUserSettings(payload));
  };

  const handleTabChange = (_, value) => {
    setActiveTab(value);
  };

  const executePurge = async () => {
    const weeks = Number(purgeWeeks);
    if (!Number.isFinite(weeks) || weeks <= 0) {
      setPurgeFeedback({
        severity: 'error',
        message: 'Please enter a positive number of weeks.'
      });
      return;
    }

    try {
      setPurgeLoading(true);
      setPurgeFeedback(null);
      const response = await userTradeSignalsAPI.purgeUserTradeSignals(weeks);
      const deletedCount = response?.data?.deletedCount ?? 0;
      setPurgeFeedback({
        severity: 'success',
        message: `Removed ${deletedCount} trade${deletedCount === 1 ? '' : 's'} older than ${weeks} week${weeks === 1 ? '' : 's'}.`
      });
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to purge old trade signals.';
      setPurgeFeedback({
        severity: 'error',
        message
      });
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleOpenPurgeDialog = () => {
    const weeks = Number(purgeWeeks);
    if (!Number.isFinite(weeks) || weeks <= 0) {
      setPurgeFeedback({
        severity: 'error',
        message: 'Please enter a positive number of weeks.'
      });
      return;
    }
    setPurgeDialogOpen(true);
  };

  const handleConfirmPurge = async () => {
    await executePurge();
    setPurgeDialogOpen(false);
  };

  const handleClosePurgeDialog = () => {
    if (!purgeLoading) {
      setPurgeDialogOpen(false);
    }
  };

  return (
    <Box p={3}>
      <Typography 
        sx={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 600,
          letterSpacing: '-0.03em',
          color: 'var(--dwlf-text)',
          mb: 1,
        }}
      >
        Settings
      </Typography>
      <Typography 
        sx={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.85rem',
          color: 'var(--dwlf-ink-50)',
          mb: 3,
        }}
      >
        Configure notifications, quotas, and preferences
      </Typography>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ 
          mb: 3,
          '& .MuiTab-root': {
            fontFamily: '"Space Mono", monospace',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            minHeight: '48px',
          },
        }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab label="Notifications" value="notifications" />
        <Tab label="Custom Events" value="customEvents" />
        <Tab label="Chart Settings" value="charts" />
        <Tab label="Trading" value="trading" />
        <Tab label="Briefing List" value="briefingList" />
        <Tab label="Quotas" value="quotas" />
        <Tab label="Data Management" value="data" />
        <Tab label="API Keys" value="apiKeys" />
      </Tabs>

      {activeTab === 'notifications' && (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TelegramBotCard />

          <SubscriptionsSection />

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Preview Features
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Opt into experimental charting upgrades (currently includes the volatility-aware trendline engine).
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={previewFeaturesEnabled}
                  onChange={handlePreviewToggle}
                  color="primary"
                  disabled={saving}
                />
              }
              label="Enable preview chart features"
            />
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Event Notifications
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose which DWLF-detected events should trigger alerts for you.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" onClick={handleClearAll} disabled={saving}>
                  Clear All
                </Button>
                <Button variant="outlined" size="small" onClick={handleSelectAll} disabled={saving}>
                  Select All
                </Button>
              </Stack>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <DWLFLoader size="medium" />
              </Box>
            ) : (
              <Stack spacing={2}>
                {categorizedIndicatorEvents.map((group) => (
                  <Box key={group.label}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: 0.5 }}
                    >
                      {group.label}
                    </Typography>
                    <Stack spacing={1.25} mt={0.5}>
                      {group.definitions.map((definition) => (
                        <FormControlLabel
                          key={definition.id}
                          sx={{ alignItems: 'flex-start', m: 0 }}
                          control={
                            <Switch
                              checked={eventNotifications.includes(definition.id)}
                              onChange={() => handleToggle(definition.id)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {definition.name}
                              </Typography>
                              {definition.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {definition.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            {saveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveError}
              </Alert>
            )}

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving || loading || !settingsSynced}
              >
                {saving ? 'Saving…' : 'Save Preferences'}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      {activeTab === 'customEvents' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Custom Event Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Choose which of your custom events should trigger notifications when they fire.
          </Typography>

          {customEventsError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {customEventsError}
            </Alert>
          )}

          {toggleError && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }} onClose={() => setToggleError(null)}>
              {toggleError}
            </Alert>
          )}

          {customEventsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <DWLFLoader size="medium" />
            </Box>
          ) : customEventsError ? null : customEvents.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={6}
              gap={2}
            >
              <Typography variant="body1" color="text.secondary" textAlign="center">
                You haven't created any custom events yet.
              </Typography>
              <Button
                component={Link}
                to="/custom-events/new"
                variant="contained"
                color="primary"
              >
                Create Your First Event
              </Button>
            </Box>
          ) : (
            <List sx={{ mt: 2 }}>
              {customEvents.map((event, index) => (
                <React.Fragment key={event.eventId}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={600}>
                          {event.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {event.description || 'No description'}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={event.notifyOnFire === true}
                        onChange={() => handleToggleNotifyOnFire(event.eventId, event.notifyOnFire)}
                        disabled={togglingEventIds.has(event.eventId)}
                        color="primary"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < customEvents.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      )}

      {activeTab === 'charts' && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Axis & Crosshair Theme
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Pick the colour palette used for chart axes and crosshair guides across DWLF charts.
            </Typography>

            <RadioGroup
              value={axisColorSchemeId}
              onChange={handleAxisColorSchemeChange}
              sx={{ mt: 2 }}
            >
              {AXIS_COLOR_SCHEMES.map((scheme) => (
                <FormControlLabel
                  key={scheme.id}
                  value={scheme.id}
                  control={<Radio color="primary" />}
                  sx={{
                    alignItems: 'flex-start',
                    border: '1px solid',
                    borderColor: axisColorSchemeId === scheme.id ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    mb: 2,
                    transition: 'border-color 0.2s ease',
                  }}
                  label={(
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {scheme.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {scheme.description}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1.5} mt={1.5}>
                        <Box
                          sx={{
                            width: 42,
                            height: 6,
                            borderRadius: 999,
                            backgroundColor: scheme.colors.dark,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Dark mode
                        </Typography>
                        <Box
                          sx={{
                            width: 42,
                            height: 6,
                            borderRadius: 999,
                            backgroundColor: scheme.colors.light,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Light mode
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              ))}
            </RadioGroup>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Semi-Live Candles
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Show a provisional in-progress candle at the edge of weekly and daily charts, built from lower timeframe data (daily → weekly, hourly → daily).
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={settings?.[USER_SETTING_SEMI_LIVE_CANDLES] !== false}
                  onChange={(_, checked) => {
                    dispatch(setLocalSetting({
                      settingKey: USER_SETTING_SEMI_LIVE_CANDLES,
                      value: checked,
                    }));
                  }}
                  color="primary"
                />
              )}
              label="Enable semi-live candles on charts"
              sx={{ mt: 1 }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Chart Animations
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Control how charts animate when loading new data. Animations provide visual feedback during data loading and chart rendering.
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={animationEnabled}
                  onChange={(_, checked) => {
                    setAnimationEnabled(checked);
                    localStorage.setItem('dwlf-animation-enabled', checked ? 'true' : 'false');
                    window.dispatchEvent(new CustomEvent('dwlf-animation-preference-changed', { 
                      detail: { enabled: checked } 
                    }));
                  }}
                  color="primary"
                />
              )}
              label="Enable chart animations"
              sx={{ mt: 1 }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Crosshair Behaviour
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Decide whether the horizontal guide snaps to chart data or tracks your cursor precisely.
            </Typography>

            <RadioGroup
              value={crosshairSnapModeId}
              onChange={handleCrosshairSnapModeChange}
              sx={{ mt: 2 }}
            >
              {CROSSHAIR_SNAP_MODES.map((mode) => (
                <FormControlLabel
                  key={mode.id}
                  value={mode.id}
                  control={<Radio color="primary" />}
                  sx={{
                    alignItems: 'flex-start',
                    border: '1px solid',
                    borderColor: crosshairSnapModeId === mode.id ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    mb: 2,
                    transition: 'border-color 0.2s ease',
                  }}
                  label={(
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {mode.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mode.description}
                      </Typography>
                    </Box>
                  )}
                />
              ))}
            </RadioGroup>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Crosshair Price Label
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Show or hide the price label that follows the crosshair on the right axis.
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={showCrosshairPriceLabel}
                  onChange={handleCrosshairPriceLabelToggle}
                  color="primary"
                />
              )}
              label="Display the price label next to the crosshair"
              sx={{ mt: 1 }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Adaptive Grid
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Toggle an auto-scaling price/time grid that densifies as you zoom in and loosens when you zoom out.
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={showAdaptiveGrid}
                  onChange={handleAdaptiveGridToggle}
                  color="primary"
                />
              )}
              label="Show adaptive grid lines on DWLF charts"
              sx={{ mt: 1 }}
            />

            <Divider sx={{ my: 3 }} />

            {isAdminUser && (
              <>
                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Admin Chart Controls
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Show the Tune and Recompute buttons on chart headers. Off by default — flip this on when you need to tinker with cycle settings or force a backend re-backfill, otherwise the chart stays uncluttered.
                </Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={showAdminChartControls}
                      onChange={handleAdminChartControlsToggle}
                      color="primary"
                    />
                  )}
                  label="Show Tune / Recompute buttons on chart"
                  sx={{ mt: 1 }}
                />
              </>
            )}

            {saveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveError}
              </Alert>
            )}

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving || !settingsSynced}
              >
                {saving ? 'Saving…' : 'Save Chart Preferences'}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      {activeTab === 'quotas' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <QuotaStatusPanel />
        </Paper>
      )}

      {activeTab === 'data' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Trade Data Management
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Remove older trade signals to keep your account tidy. This operation permanently deletes records and cannot be undone.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              mt: 2
            }}
          >
            <TextField
              label="Delete trades older than"
              type="number"
              value={purgeWeeks}
              onChange={(event) => setPurgeWeeks(event.target.value)}
              inputProps={{ min: 1 }}
              sx={{ width: { xs: '100%', sm: 200 } }}
            />
            <Typography variant="body2" color="text.secondary">
              weeks
            </Typography>
            <Button
              variant="contained"
              color="error"
              onClick={handleOpenPurgeDialog}
              disabled={purgeLoading}
            >
              {purgeLoading ? 'Clearing…' : 'Clear Old Trades'}
            </Button>
          </Box>
          {purgeFeedback && purgeFeedback.severity === 'error' && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {purgeFeedback.message}
            </Alert>
          )}
        </Paper>
      )}

      {activeTab === 'trading' && (
        <TradingDefaultsTab />
      )}

      {activeTab === 'briefingList' && (
        <BriefingListTab />
      )}

      {activeTab === 'apiKeys' && (
        <ApiKeysTab />
      )}

      <Snackbar
        open={Boolean(purgeFeedback && purgeFeedback.severity === 'success')}
        autoHideDuration={4000}
        onClose={() => setPurgeFeedback(null)}
      >
        <Alert onClose={() => setPurgeFeedback(null)} severity="success" sx={{ width: '100%' }}>
          {purgeFeedback?.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={purgeDialogOpen}
        onClose={handleClosePurgeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clear Old Trades</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all of your trade signals older than {purgeWeeks} week{Number(purgeWeeks) === 1 ? '' : 's'}.
            This action cannot be undone. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePurgeDialog} disabled={purgeLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPurge}
            color="error"
            variant="contained"
            disabled={purgeLoading}
          >
            {purgeLoading ? 'Clearing…' : 'Delete Trades'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserSettingsPage;
